import { useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Badge } from './badge'
// Note: Date range filter temporarily disabled - requires additional dependencies

// Base filter types
export interface FilterOption {
  value: string
  label: string
  count?: number
}

export interface DateRange {
  from?: Date
  to?: Date
  preset?: string
}

export interface FilterState {
  search: string
  selectedOptions: Record<string, string[]>
  dateRange: DateRange | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

// Filter component props
export interface FilterBarProps {
  filters: FilterConfig[]
  onChange: (filters: FilterState) => void
  initialState?: Partial<FilterState>
  className?: string
}

export interface FilterConfig {
  key: string
  label: string
  type: 'search' | 'select' | 'multiselect' | 'daterange' | 'sort'
  options?: FilterOption[]
  placeholder?: string
}

// Main filter bar component
export function FilterBar({ filters, onChange, initialState, className }: FilterBarProps) {
  const [filterState, setFilterState] = useState<FilterState>({
    search: '',
    selectedOptions: {},
    dateRange: null,
    sortBy: '',
    sortOrder: 'desc',
    ...initialState,
  })

  const updateFilter = (updates: Partial<FilterState>) => {
    const newState = { ...filterState, ...updates }
    setFilterState(newState)
    onChange(newState)
  }

  const clearFilters = () => {
    const clearedState: FilterState = {
      search: '',
      selectedOptions: {},
      dateRange: null,
      sortBy: '',
      sortOrder: 'desc',
    }
    setFilterState(clearedState)
    onChange(clearedState)
  }

  const hasActiveFilters = filterState.search ||
    Object.values(filterState.selectedOptions).some(options => options.length > 0) ||
    filterState.dateRange

  return (
    <div className={`flex flex-wrap items-center gap-2 p-4 bg-muted/50 rounded-lg ${className}`}>
      {filters.map((filter) => (
        <FilterComponent
          key={filter.key}
          config={filter}
          state={filterState}
          onChange={updateFilter}
        />
      ))}

      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={clearFilters}>
          <X className="w-4 h-4 mr-1" />
          Clear Filters
        </Button>
      )}
    </div>
  )
}

// Individual filter component
function FilterComponent({
  config,
  state,
  onChange
}: {
  config: FilterConfig
  state: FilterState
  onChange: (updates: Partial<FilterState>) => void
}) {
  switch (config.type) {
    case 'search':
      return (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={config.placeholder || 'Search...'}
            value={state.search}
            onChange={(e) => onChange({ search: e.target.value })}
            className="pl-9 w-64"
          />
        </div>
      )

    case 'select':
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">{config.label}:</Label>
          <Select
            value={state.selectedOptions[config.key]?.[0] || ''}
            onValueChange={(value) => onChange({
              selectedOptions: {
                ...state.selectedOptions,
                [config.key]: value ? [value] : []
              }
            })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder={`Select ${config.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {config.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                  {option.count !== undefined && (
                    <span className="text-muted-foreground ml-2">({option.count})</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )

    case 'multiselect':
      // Temporarily simplified - popover functionality disabled
      return (
        <div className="text-xs text-muted-foreground">
          {config.label}: Multi-select (disabled)
        </div>
      )

    case 'daterange':
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Date:</Label>
          <Select
            value={state.dateRange?.preset || ''}
            onValueChange={(value) => {
              let dateRange: { from?: Date; to?: Date } | null = null;
              const now = new Date();

              switch (value) {
                case 'today':
                  dateRange = {
                    from: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
                    to: now
                  };
                  break;
                case 'yesterday':
                  const yesterday = new Date(now);
                  yesterday.setDate(yesterday.getDate() - 1);
                  dateRange = {
                    from: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()),
                    to: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59)
                  };
                  break;
                case 'this_week':
                  const weekStart = new Date(now);
                  weekStart.setDate(now.getDate() - now.getDay());
                  dateRange = { from: weekStart, to: now };
                  break;
                case 'this_month':
                  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                  dateRange = { from: monthStart, to: now };
                  break;
                case 'last_7_days':
                  const last7Days = new Date(now);
                  last7Days.setDate(now.getDate() - 7);
                  dateRange = { from: last7Days, to: now };
                  break;
                case 'last_30_days':
                  const last30Days = new Date(now);
                  last30Days.setDate(now.getDate() - 30);
                  dateRange = { from: last30Days, to: now };
                  break;
                default:
                  dateRange = null;
              }

              onChange({
                dateRange: dateRange ? { ...dateRange, preset: value } : null
              });
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_7_days">Last 7 Days</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )

    case 'sort':
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Sort by:</Label>
          <Select
            value={state.sortBy}
            onValueChange={(value) => onChange({ sortBy: value })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {config.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc' })}
          >
            {state.sortOrder === 'asc' ? '↑' : '↓'}
          </Button>
        </div>
      )

    default:
      return null
  }
}

// Active filters display component
export function ActiveFilters({
  filters,
  filterState,
  onRemove
}: {
  filters: FilterConfig[]
  filterState: FilterState
  onRemove: (filterKey: string, value?: string) => void
}) {
  const activeFilters: Array<{ key: string; label: string; value?: string }> = []

  // Search filter
  if (filterState.search) {
    activeFilters.push({
      key: 'search',
      label: `Search: "${filterState.search}"`
    })
  }

  // Selected options
  Object.entries(filterState.selectedOptions).forEach(([key, values]) => {
    if (values.length > 0) {
      const config = filters.find(f => f.key === key)
      values.forEach(value => {
        const option = config?.options?.find(o => o.value === value)
        activeFilters.push({
          key,
          label: `${config?.label}: ${option?.label || value}`,
          value
        })
      })
    }
  })

  // Date range
  if (filterState.dateRange) {
    const { from, to, preset } = filterState.dateRange
    let dateLabel = ''
    if (preset) {
      const presetLabels: Record<string, string> = {
        'today': 'Today',
        'yesterday': 'Yesterday',
        'this_week': 'This Week',
        'this_month': 'This Month',
        'last_7_days': 'Last 7 Days',
        'last_30_days': 'Last 30 Days'
      }
      dateLabel = presetLabels[preset] || 'Date range'
    } else if (from && to) {
      dateLabel = `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`
    } else if (from) {
      dateLabel = `From ${from.toLocaleDateString()}`
    } else if (to) {
      dateLabel = `Until ${to.toLocaleDateString()}`
    }
    activeFilters.push({
      key: 'daterange',
      label: `Date: ${dateLabel}`
    })
  }

  if (activeFilters.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm text-muted-foreground">Active filters:</span>
      {activeFilters.map((filter) => (
        <Badge key={`${filter.key}-${filter.value}`} variant="secondary" className="gap-1">
          {filter.label}
          <X
            className="w-3 h-3 cursor-pointer hover:text-destructive"
            onClick={() => onRemove(filter.key, filter.value)}
          />
        </Badge>
      ))}
    </div>
  )
}

// Utility functions for filtering data
export function applyFilters<T>(
  items: T[],
  filters: FilterState,
  filterFunctions: Record<string, (item: T, value: any, extra?: any) => boolean | number>
): T[] {
  let filtered = [...items]

  // Apply search filter
  if (filters.search) {
    filtered = filtered.filter(item =>
      JSON.stringify(item).toLowerCase().includes(filters.search.toLowerCase())
    )
  }

  // Apply selected options filters
  Object.entries(filters.selectedOptions).forEach(([key, values]) => {
    if (values.length > 0 && filterFunctions[key]) {
      filtered = filtered.filter(item =>
        values.some((value: any) => {
          const result = filterFunctions[key](item, value)
          return typeof result === 'boolean' ? result : true
        })
      )
    }
  })

  // Apply date range filter (requires dateField in filterFunctions)
  if (filters.dateRange && filterFunctions.dateRange) {
    filtered = filtered.filter(item => {
      const result = filterFunctions.dateRange(item, filters.dateRange)
      return typeof result === 'boolean' ? result : true
    })
  }

  // Apply sorting
  if (filters.sortBy && filterFunctions.sort) {
    filtered.sort((a, b) => {
      const result = filterFunctions.sort(a, b, filters.sortBy)
      const numericResult = typeof result === 'number' ? result : 0
      return filters.sortOrder === 'asc' ? numericResult : -numericResult
    })
  }

  return filtered
}